'use client';

import { useTranslate } from '@tolgee/react';
import Image from 'next/image';
import Link from 'next/link';
import { Suspense, useEffect, useRef } from 'react';

import logo from '@/assets/logo.png';
import { selectStyles } from '@/components/styles/select-styles';
import { type UserRole } from '@/db/schema/users';
import { getLink, AdminPages, Pages } from '@/lib/app-links';
import { useUser } from '@/lib/auth-hooks';
import { logError } from '@/lib/logError';
import { cn } from '@/lib/utils';

import { HeaderAuthLinks } from './HeaderAuthLinks';
import { LanguageSelector } from './LanguageSelector';

interface PageHeaderProps {
  isOffline: boolean;
}

export default function PageHeader({ isOffline }: PageHeaderProps) {
  const { t } = useTranslate();
  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
  const { isLoaded, isSignedIn, user } = useUser();
  const userRole = user?.publicMetadata?.role as UserRole | undefined;
  const hasLoggedInconsistentAuthRef = useRef(false);

  useEffect(() => {
    const hasInconsistentAuthState = isLoaded && isSignedIn && !user;

    if (hasInconsistentAuthState && !hasLoggedInconsistentAuthRef.current) {
      hasLoggedInconsistentAuthRef.current = true;
      void logError({
        message: 'User is null despite auth being loaded',
        context: { isOffline, isLoaded, isSignedIn, source: 'PageHeader' },
      });
    }

    if (!hasInconsistentAuthState) {
      hasLoggedInconsistentAuthRef.current = false;
    }
  }, [isLoaded, isSignedIn, user, isOffline]);
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

  return (
    <nav className="relative z-50">
      <div className="flex flex-wrap items-center justify-between gap-4 py-4 sm:py-5">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <Link href="/" className="flex items-center space-x-3 rtl:space-x-reverse">
            <Image
              src={logo}
              className="size-8"
              alt={t('component.page-header.logo-alt', 'bettr.golf Logo')}
              width={32}
              height={32}
            />
            <span className="self-center whitespace-nowrap text-xl font-semibold tracking-tight text-white sm:text-2xl">
              <i>bettr</i>.golf
            </span>
          </Link>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3 sm:gap-5">
          <div className="flex items-center gap-1 sm:gap-2">
            {isOffline ? (
              // In offline mode, render links directly without SignedIn component
              <>
                <Link href={getLink(Pages.Home)} className={cn(selectStyles.darkLink, 'hidden sm:inline-flex')}>
                  Dashboard
                </Link>
                <Link href={getLink(AdminPages.Admin)} className={cn(selectStyles.darkLink)}>
                  Admin
                </Link>
              </>
            ) : (
              <>
                {isLoaded && isSignedIn && userRole && (userRole === 'user' || userRole === 'admin') && (
                  <Link href={getLink(Pages.Home)} className={cn(selectStyles.darkLink, 'hidden sm:inline-flex')}>
                    Dashboard
                  </Link>
                )}
                {isLoaded && isSignedIn && userRole === 'admin' && (
                  <Link href={getLink(AdminPages.Admin)} className={cn(selectStyles.darkLink)}>
                    Admin
                  </Link>
                )}
              </>
            )}
          </div>
          <Suspense>
            <div>
              <LanguageSelector />
            </div>
          </Suspense>
          <div className="flex h-8 items-center justify-end">
            <HeaderAuthLinks isOffline={isOffline} />
          </div>
        </div>
      </div>
    </nav>
  );
}
